import { Card } from "@/components/ui/card";
import { Link } from "@tanstack/react-router";
import { Banknote, Leaf, MapPinIcon, RefreshCwIcon, SearchCheckIcon, ThumbsUp, WrenchIcon } from "lucide-react";
import { ReactNode, useState } from "react";
import {
  Opportunity,
  getEffectiveSeverity,
  opportunityCategorySeverityBackgroundClass
} from "@/opportunity/opportunity";
import { differenceInHours, format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import UsefulFeedbackModal from "@/opportunity/UsefulFeedbackModal";

type ActivityChange = "new_issue" | "degraded" | null;

const getActivityChange = (opp: Opportunity): ActivityChange => {
  const now = new Date();
  const hoursThreshold = 24;

  const isNew = differenceInHours(now, parseISO(opp.identified_date)) < hoursThreshold;
  if (isNew) return "new_issue";

  const hsiDatetime = opp.highest_severity_detection_datetime ?? opp.highest_severity_instance?.detection_datetime;
  if (hsiDatetime) {
    const isRecent = differenceInHours(now, parseISO(hsiDatetime)) < hoursThreshold;
    if (isRecent) return "degraded";
  }

  return null;
};

const ActivityBadge = ({ change }: { change: ActivityChange }) => {
  if (!change) return null;
  if (change === "new_issue") {
    return (
      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-600">
        New
      </span>
    );
  }
  return (
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-600">
      Severity ↑
    </span>
  );
};

const OpportunityCardBadge = ({ children, className }: { children: ReactNode, className?: string }) => (
  <div className={cn("text-xs flex flex-row items-center gap-1 bg-gray-50 rounded-lg px-2 py-1", className)}>
    {children}
  </div>
)

const SavingsBadges = ({ opportunity }: { opportunity: Opportunity }) => {
  const frp_usd = opportunity.savings.find(
    saving => saving.name === "FRP" && saving.unit === "$" && !!saving.value
  );
  const erp_tons = opportunity.savings.find(
    saving => saving.name === "ERP" && saving.unit === "tons CO2e" && !!saving.value
  );

  if (!frp_usd || !erp_tons) return;

  return (
    <>
      {
        frp_usd && (
          <OpportunityCardBadge className="bg-blue-50">
            <Banknote size={16}/>
            <span className="font-normal">Energy savings:</span>
            <span className="font-bold">${frp_usd.value.toLocaleString()}</span>
          </OpportunityCardBadge>
        )
      }
      {
        erp_tons && (
          <OpportunityCardBadge className="bg-green-50">
            <Leaf size={16}/>
            <span className="font-normal">Emissions savings:</span>
            <span className="font-bold">{erp_tons.value.toLocaleString()} tons CO2e</span>
          </OpportunityCardBadge>
        )
      }
    </>
  )
}

export const OpportunityCard = ({ opp }: { opp: Opportunity }) => {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const activityChange = getActivityChange(opp);

  return (
    <>
      <Link
        to={"/organization/$organizationId/issues/$opportunityId"}
        params={{ opportunityId: opp.id, organizationId: opp.organization_id }}
        className="inline-block whitespace-nowrap print:hidden"
      >
        <Card className="flex flex-row justify-between overflow-hidden">
          {
            getEffectiveSeverity(opp) && (
              <div className={`w-[6px] shrink-0 ${opportunityCategorySeverityBackgroundClass[getEffectiveSeverity(opp)!]}`}/>
            )
          }
          <div className="grow flex flex-col">
            <div className="flex flex-row justify-between items-start">
              <h3 className="relative p-2 whitespace-normal">
                {opp.name}
              </h3>
              <div className="shrink flex items-center gap-1.5 px-2 py-1">
                <ActivityBadge change={activityChange} />
                <span className="bg-gray-50 px-2 py-0.5 rounded-lg text-xs">
                  {opp.status}
                </span>
              </div>
            </div>
            <div className="p-2 flex flex-wrap gap-2 items-center">
              <OpportunityCardBadge>
                <MapPinIcon size={16}/>
                {opp.location.name}
              </OpportunityCardBadge>
              <OpportunityCardBadge>
                <SearchCheckIcon size={16}/>
                Identified: {format(parseISO(opp.identified_date), "LLL do, yyyy")}
              </OpportunityCardBadge>
              {opp.last_detected_date && (
                <OpportunityCardBadge>
                  <RefreshCwIcon size={16}/>
                  Last detected: {format(parseISO(opp.last_detected_date), "LLL do, yyyy")}
                </OpportunityCardBadge>
              )}
              {
                opp.category_object.failure_causes && opp.category_object.failure_causes.some(cause => !!cause.required_skill) && (
                  <OpportunityCardBadge className="bg-yellow-100">
                    <WrenchIcon size={16}/>
                    Required skill: {opp.category_object.failure_causes.filter(cause => !!cause.required_skill)[0].required_skill}
                  </OpportunityCardBadge>
                )
              }
              {
                opp.savings && (
                  <SavingsBadges opportunity={opp}/>
                )
              }
              <button
                className="ml-auto p-1.5 rounded-md text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setFeedbackOpen(true);
                }}
                title="Give feedback"
              >
                <ThumbsUp size={14} />
              </button>
            </div>
          </div>
        </Card>
      </Link>
      {feedbackOpen && (
        <UsefulFeedbackModal
          opportunity={opp}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
    </>
  );
};
